import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TrendingUp, Target, Gift, Download, Receipt, Percent, Play, Share2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import CommissionPreviewWidget from '../../components/Affiliate/CommissionPreviewWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AffiliateDashboardNew = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [copiedBtn, setCopiedBtn] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoShareUrl, setDemoShareUrl] = useState('');
  const [demoShareCopied, setDemoShareCopied] = useState(false);

  useEffect(() => {
    checkAuthAndFetchData();
  }, []);

  const checkAuthAndFetchData = async () => {
    const token = localStorage.getItem('affiliate_token');
    
    if (!token) {
      navigate('/affiliate/login');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/affiliate/dashboard`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setData(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('affiliate_token');
        localStorage.removeItem('affiliate_data');
        navigate('/affiliate/login');
      } else {
        setError(err.response?.data?.detail || 'Fehler beim Laden der Daten');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('affiliate_token');
    localStorage.removeItem('affiliate_data');
    navigate('/affiliate/login');
  };

  const copyToClipboard = async (text, btnId = '') => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        setCopiedBtn(btnId);
        setTimeout(() => { setCopied(false); setCopiedBtn(''); }, 2000);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          textArea.remove();
          setCopied(true);
          setCopiedBtn(btnId);
          setTimeout(() => { setCopied(false); setCopiedBtn(''); }, 2000);
        } catch (err) {
          textArea.remove();
        }
      }
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  // ── Demo Mode ──────────────────────────────────────────────────────────────
  const handleStartDemo = async () => {
    setDemoLoading(true);
    try {
      const token = localStorage.getItem('affiliate_token') || localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/auth/demo-token`,
        { token_type: 'direct' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Open demo in new tab
      window.open(res.data.demo_url, '_blank');
    } catch (err) {
      console.error('Demo start failed:', err);
      alert('Demo konnte nicht gestartet werden. Bitte versuche es erneut.');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleGetShareableLink = async () => {
    setDemoLoading(true);
    try {
      const token = localStorage.getItem('affiliate_token') || localStorage.getItem('token');
      const res = await axios.post(
        `${API_URL}/api/auth/demo-token`,
        { token_type: 'shareable' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDemoShareUrl(res.data.demo_url);
      await navigator.clipboard.writeText(res.data.demo_url).catch(() => {});
      setDemoShareCopied(true);
      setTimeout(() => setDemoShareCopied(false), 3000);
    } catch (err) {
      console.error('Share link failed:', err);
      alert('Teilbarer Link konnte nicht erstellt werden.');
    } finally {
      setDemoLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatDate = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Lade Dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
            <svg className="h-10 w-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Fehler</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <button
            onClick={handleLogout}
            className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Zurück zum Login
          </button>
        </div>
      </div>
    );
  }

  const { affiliate, customers, active_customers_count, monthly_commissions, recent_payments, current_month_earnings, current_month_revenue, open_adjustments_total } = data;
  const referralLink  = `${window.location.origin}/register-enhanced?ref=${affiliate.affiliate_code}`;
  const dealLink      = `${window.location.origin}/register-enhanced?ref=${affiliate.affiliate_code}&deal=true`;
  const winbackLink   = `${window.location.origin}/reactivate?ref=${affiliate.affiliate_code}`;
  
  // Calculate lifetime earnings
  const lifetimeEarnings = affiliate.total_earnings || 0;
  const currentMonthRevenue = current_month_revenue || 0;

  // Prepare chart data
  const chartData = monthly_commissions.slice(-6).map(comm => ({
    period: comm.period,
    revenue: comm.revenue_total_net || 0,
    commission: comm.commission_amount || 0
  }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4 sm:p-6 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8 mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Affiliate Dashboard
              </h1>
              <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                Willkommen zurück, {affiliate.first_name} {affiliate.last_name}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={() => navigate('/affiliate/profile')}
                className="px-4 py-2 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors min-h-[44px]"
              >
                Bankdaten
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px]"
              >
                Abmelden
              </button>
            </div>
          </div>

          {/* Affiliate Code & Links */}
          <div className="mt-6 space-y-3">
            {/* Standard-Link */}
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-2 gap-2">
                <div>
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Standard-Link
                  </span>
                  <span className="ml-2 px-2 py-0.5 bg-blue-200 text-blue-800 dark:bg-blue-800 dark:text-blue-200 rounded text-xs">
                    Standard-Link
                  </span>
                </div>
                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                  {affiliate.affiliate_code}
                </span>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-400 mb-2">
                Neukunden erhalten ab 29,90 € Netto/Monat (Starter). Monatlich kündbar.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  data-testid="referral-link-input"
                  className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg"
                />
                <button
                  onClick={() => copyToClipboard(referralLink, 'referral')}
                  data-testid="copy-referral-link"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium min-h-[44px]"
                >
                  {copiedBtn === 'referral' ? '✓ Kopiert!' : 'Kopieren'}
                </button>
              </div>
            </div>

            {/* Deal-Link entfernt: Deal-/50%-Aktion ist deaktiviert (Alt-Angebot). */}

            {/* Win-Back Link */}
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                  Win-Back Link (für ehemalige Kunden)
                </span>
                <span className="px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-bold">Ohne Trial</span>
              </div>
              <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">
                Teile diesen Link mit ehemaligen AllFlowX-Kunden, die du zurückgewinnen möchtest.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch gap-2">
                <input
                  type="text"
                  value={winbackLink}
                  readOnly
                  data-testid="winback-link-input"
                  className="flex-1 px-3 py-2 text-sm bg-white dark:bg-amber-50 border border-amber-300 dark:border-amber-700 rounded-lg"
                />
                <button
                  onClick={() => copyToClipboard(winbackLink, 'winback')}
                  data-testid="copy-winback-link"
                  className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-sm font-medium min-h-[44px]"
                >
                  {copiedBtn === 'winback' ? '✓ Kopiert!' : 'Kopieren'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-x-auto">
          <div className="flex border-b border-gray-200 dark:border-gray-700 min-w-max">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'dashboard'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Dashboard
            </button>
            <button
              onClick={() => setActiveTab('billing')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'billing'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Receipt className="w-4 h-4" />
              Abrechnung
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`flex items-center gap-2 px-4 sm:px-6 py-3 sm:py-4 font-medium transition-colors whitespace-nowrap ${
                activeTab === 'campaigns'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <Gift className="w-4 h-4" />
              Kampagnen
            </button>
          </div>
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <>
            {/* Commission Preview Widget - prominent at top */}
            <CommissionPreviewWidget
              currentMonthRevenue={currentMonthRevenue}
              currentMonthEarnings={current_month_earnings}
            />

            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-6">
              {/* Revenue & Commission Chart */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
                  Umsatz & Provision (Verlauf)
                </h2>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="period" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Umsatz" />
                      <Line type="monotone" dataKey="commission" stroke="#10b981" strokeWidth={2} name="Provision" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                    Noch keine Daten vorhanden
                  </p>
                )}
              </div>

              {/* Monthly Goal Card */}
              <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl shadow-xl p-4 sm:p-6 text-white">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-5 sm:w-6 h-5 sm:h-6" />
                  <h2 className="text-base sm:text-lg font-bold">Zusammenfassung</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <p className="text-xs sm:text-sm opacity-90 mb-1">Gesamteinnahmen (Lifetime)</p>
                    <p className="text-2xl sm:text-3xl font-bold">{formatCurrency(lifetimeEarnings)}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs sm:text-sm opacity-90 mb-1">Aktive Kunden</p>
                    <p className="text-2xl sm:text-3xl font-bold">{active_customers_count}</p>
                  </div>

                  <div className="pt-4 border-t border-white/20">
                    <p className="text-xs sm:text-sm opacity-80">Provisionsperioden gesamt</p>
                    <p className="text-lg font-bold mt-1">{monthly_commissions.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Monthly Commissions Table */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
                📊 Provisionsübersicht
              </h2>
              
              {open_adjustments_total < 0 && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400" data-testid="aff-carryforward-banner">
                  ⚠️ Offener Negativübertrag aus Erstattung/Storno: <strong>{formatCurrency(open_adjustments_total)}</strong> — wird mit deiner nächsten Provision verrechnet.
                </div>
              )}
              {monthly_commissions.length > 0 ? (                <div className="overflow-x-auto">
                  <table className="w-full min-w-[500px]">
                    <thead className="border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Periode</th>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Umsatz</th>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 hidden sm:table-cell">Tier</th>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Provision</th>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Korrektur</th>
                        <th className="text-left py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {monthly_commissions.map((commission) => (
                        <tr key={commission.id} className="border-b border-gray-100 dark:border-gray-800">
                          <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 dark:text-gray-100 font-medium">
                            {commission.period}
                          </td>
                          <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 dark:text-gray-100">
                            {formatCurrency(commission.revenue_total_net)}
                          </td>
                          <td className="py-3 px-2 sm:px-4 hidden sm:table-cell">
                            <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded">
                              {commission.tier_percent}
                            </span>
                          </td>
                          <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm font-bold text-green-600 dark:text-green-400" data-testid={`aff-commission-amount-${commission.period}`}>
                            {formatCurrency(commission.commission_amount)}
                          </td>
                          <td className="py-3 px-2 sm:px-4 text-xs sm:text-sm" data-testid={`aff-commission-correction-${commission.period}`}>
                            {commission.storno_deduction > 0 ? (
                              <span className="text-red-600 dark:text-red-400 font-medium" title="Erstattung/Storno bereits verrechnet">
                                −{formatCurrency(commission.storno_deduction)}
                              </span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-3 px-2 sm:px-4">
                            {commission.on_hold ? (
                              <span className="px-2 py-1 text-xs font-medium rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400" data-testid={`aff-commission-hold-${commission.period}`} title="Gesperrt wegen offenem Chargeback">
                                🔒 Gesperrt
                              </span>
                            ) : (
                            <span className={`px-2 py-1 text-xs font-medium rounded ${
                              commission.status === 'paid' ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400' :
                              commission.status === 'approved' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400' :
                              commission.status === 'storno' ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400' :
                              'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400'
                            }`}>
                              {commission.status === 'paid' ? 'Ausgezahlt' :
                               commission.status === 'approved' ? 'Genehmigt' :
                               commission.status === 'storno' ? 'Storniert' :
                               'Berechnet'}
                            </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-600 dark:text-gray-400 text-center py-8">
                  Noch keine Provisionen berechnet
                </p>
              )}
            </div>
          </>
        )}

        {/* Billing Tab */}
        {activeTab === 'billing' && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-4 sm:p-6 md:p-8">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-6">
              💳 Gutschriften (Self-Billing)
            </h2>
            
            <div className="space-y-4">
              {monthly_commissions.filter(c => c.status === 'paid').length > 0 ? (
                monthly_commissions.filter(c => c.status === 'paid').map((comm) => (
                  <div key={comm.id} className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors gap-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg flex-shrink-0">
                        <Receipt className="w-5 sm:w-6 h-5 sm:h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-sm sm:text-base text-gray-900 dark:text-gray-100">
                          Gutschrift {comm.period}
                        </p>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                          {formatCurrency(comm.commission_amount)} • Ausgezahlt am {formatDate(comm.payout_date)}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        const token = localStorage.getItem('affiliate_token');
                        const url = `${API_URL}/api/affiliate/gutschriften/${comm.gutschrift_number}/pdf`;
                        
                        fetch(url, {
                          headers: {
                            'Authorization': `Bearer ${token}`
                          }
                        })
                        .then(response => response.blob())
                        .then(blob => {
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `Gutschrift_${comm.gutschrift_number}_${comm.period}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          window.URL.revokeObjectURL(url);
                          document.body.removeChild(a);
                        })
                        .catch(error => {
                          console.error('Download error:', error);
                          alert('Fehler beim Download');
                        });
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] w-full sm:w-auto justify-center"
                    >
                      <Download className="w-4 h-4" />
                      PDF
                    </button>
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                  Noch keine Gutschriften vorhanden
                </p>
              )}
            </div>
          </div>
        )}

        {/* Campaigns Tab */}
        {activeTab === 'campaigns' && (
          <div className="space-y-6">
            {/* Demo-Modus Card */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 p-5 sm:p-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-xl">
                    <Play className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg sm:text-xl font-bold text-white">Demo-Modus</h2>
                    <p className="text-orange-100 text-sm">Zeige AllFlowX live – ohne echte Daten</p>
                  </div>
                </div>
              </div>

              <div className="p-5 sm:p-6 space-y-5">
                {/* Description */}
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl p-4">
                  <p className="text-sm text-orange-800 dark:text-orange-300">
                    Die Sandbox-Umgebung enthält Demo-Produkte, TGTG-Bestellungen, Temperaturmessungen und mehr –
                    alle Änderungen sind <strong>temporär</strong> und beeinflussen keine echten Daten.
                  </p>
                </div>

                {/* Button 1: Own demo */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Demo selbst starten</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Öffnet in neuem Tab · 60 Min. Session · Vollständig interaktiv
                      </p>
                    </div>
                    <button
                      onClick={handleStartDemo}
                      disabled={demoLoading}
                      data-testid="start-demo-button"
                      className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition-colors min-h-[44px] w-full sm:w-auto justify-center"
                    >
                      <Play className="w-4 h-4" />
                      {demoLoading ? 'Startet...' : 'Demo starten'}
                    </button>
                  </div>
                </div>

                {/* Button 2: Shareable link */}
                <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Teilbaren Demo-Link erstellen</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Link für Interessenten · 48 Std. gültig · Bis zu 50 Nutzungen
                      </p>
                    </div>
                    <button
                      onClick={handleGetShareableLink}
                      disabled={demoLoading}
                      data-testid="get-shareable-demo-link"
                      className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-xl font-semibold text-sm transition-colors min-h-[44px] w-full sm:w-auto justify-center"
                    >
                      <Share2 className="w-4 h-4" />
                      {demoLoading ? 'Erstelle...' : 'Link generieren'}
                    </button>
                  </div>

                  {/* Show generated link */}
                  {demoShareUrl && (
                    <div className="mt-3 flex flex-col sm:flex-row items-stretch gap-2">
                      <input
                        type="text"
                        value={demoShareUrl}
                        readOnly
                        data-testid="shareable-demo-link-input"
                        className="flex-1 px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded-lg"
                      />
                      <button
                        onClick={() => { navigator.clipboard.writeText(demoShareUrl); setDemoShareCopied(true); setTimeout(() => setDemoShareCopied(false), 2000); }}
                        data-testid="copy-shareable-demo-link"
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        {demoShareCopied ? '✓ Kopiert!' : 'Kopieren'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Marketing Materials placeholder */}
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-5 sm:p-6">
              <div className="flex items-center gap-3 mb-4">
                <Gift className="w-5 h-5 text-purple-600" />
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-gray-100">
                  Marketing-Materialien
                </h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                Exklusive Materialien und Aktionen folgen bald.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDashboardNew;
